import React, { useMemo, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import {
  Container, Box, Typography, Chip, TextField, MenuItem, CircularProgress,
  Collapse, IconButton, Tooltip, Button, InputAdornment,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SearchIcon from '@mui/icons-material/Search'
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining'
import StorefrontIcon from '@mui/icons-material/Storefront'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import PaymentsIcon from '@mui/icons-material/Payments'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import { getQorTransactions } from '../apollo/queries'

const GET_TXNS = gql`${getQorTransactions}`

// ── Visual language ─────────────────────────────────────────────────────────
// Platform (RushmyFood) = indigo, Restaurant = emerald. Everything on this page
// keeps that mapping so the split reads at a glance.
const PLATFORM = '#5b5bd6'
const RESTAURANT = '#12a594'
const INK = '#1c2024'
const MUTED = '#697177'
const LINE = '#e8eaed'

const STATUS_META = {
  PENDING: { label: 'Processed · awaiting settlement', dot: '#f5a524', bg: '#fdf4e3', fg: '#8a5a00' },
  SETTLED: { label: 'Settled', dot: '#17a34a', bg: '#e9f6ee', fg: '#166534' },
  REFUNDED: { label: 'Refunded', dot: '#e5484d', bg: '#fdebec', fg: '#a12226' },
  VOIDED: { label: 'Voided', dot: '#8b8d98', bg: '#f0f0f3', fg: '#5a5c66' },
  COD_OWED: { label: 'Cash · commission owed', dot: '#0091ff', bg: '#e7f2fd', fg: '#0b5394' },
  COD_COLLECTED: { label: 'Cash · collected', dot: '#12a594', bg: '#e6f5f3', fg: '#0b6e63' },
}

const SCENARIO_META = {
  PLATFORM_DELIVERY: { label: 'We deliver', Icon: DeliveryDiningIcon },
  RESTAURANT_DELIVERY: { label: 'Restaurant delivers', Icon: StorefrontIcon },
  PICKUP: { label: 'Pickup', Icon: ShoppingBagIcon },
  COD: { label: 'Cash on delivery', Icon: PaymentsIcon },
}

const usd = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`)

// One "label …dotted leader… amount" line inside a share panel.
function MoneyRow({ label, value, hint, strong, color }) {
  if (!strong && (value == null || Number(value) === 0)) return null
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.4 }}>
      {/* Label may wrap; only the amount is nowrap — long hints must never push
          the value outside the panel. */}
      <Typography variant="body2" sx={{ color: strong ? INK : MUTED, fontWeight: strong ? 700 : 400, minWidth: 0 }}>
        {label}
        {hint && (
          <Typography component="span" variant="caption" sx={{ color: MUTED, ml: 0.6 }}>{hint}</Typography>
        )}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 12, borderBottom: `1px dotted ${LINE}`, transform: 'translateY(-4px)' }} />
      <Typography variant="body2" sx={{
        fontWeight: strong ? 800 : 600, color: color || INK,
        fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
      }}>
        {usd(value)}
      </Typography>
    </Box>
  )
}

// The expanded per-transaction breakdown: customer receipt → split bar → shares.
function SplitDetail({ t }) {
  const pb = t.platformBreakdown || {}
  const rb = t.restaurantBreakdown || {}
  const total = Number(t.grandTotal) || 0
  // Delivery fallback: this order was sold (and split) as restaurant-delivery
  // but WE delivered. The recorded debt = fee + tip + the commission difference
  // vs the we-deliver rate; derive the effective rate for the strikethrough.
  const fellBack = Number(t.fallbackDebt) > 0
  const fbFeeTip = Number(rb.deliveryFee || 0) + Number(rb.deliveryTip || 0)
  const fbDelta = fellBack ? Math.max(0, Number(t.fallbackDebt) - fbFeeTip) : 0
  const effRate = fbDelta > 0.005 && Number(t.subtotal) > 0
    ? Math.round(((Number(t.commissionAmount || 0) + fbDelta) / Number(t.subtotal)) * 100)
    : null
  const rateHint = (suffix) => {
    if (t.commissionRate == null) return null
    if (effRate != null) {
      return (
        <>
          (<s style={{ color: '#9ca3af' }}>{Number(t.commissionRate)}%</s>
          {' '}<span style={{ color: '#a12226', fontWeight: 700 }}>{effRate}%</span> {suffix})
        </>
      )
    }
    return `(${Number(t.commissionRate)}% ${suffix})`
  }
  // Effective amounts after any debt recovery withheld from the restaurant.
  const recovered = Number(t.debtRecovered) || 0
  const restEff = Number(t.restaurantCut) - recovered
  const platEff = Number(t.platformCut) + recovered
  const restPct = total > 0 ? Math.round((restEff / total) * 100) : 0
  const platPct = total > 0 ? 100 - restPct : 0
  const hasSplit = t.platformCut != null && t.restaurantCut != null

  if (!hasSplit) {
    return (
      <Typography variant="body2" sx={{ color: MUTED, px: 3, pb: 2.5 }}>
        No split recorded for this transaction (older order or membership charge).
      </Typography>
    )
  }

  const panel = {
    flex: 1, minWidth: 260, borderRadius: 2.5, p: 2.25,
    border: `1px solid ${LINE}`, bgcolor: '#fff',
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, pb: 2.5 }}>
      {/* Proportion bar: how the customer's total divides between the two parties */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: RESTAURANT }}>
            Restaurant {usd(restEff)} · {restPct}%
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: PLATFORM }}>
            RushmyFood {usd(platEff)} · {platPct}%
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: '#f0f0f3' }}>
          <Box sx={{ width: `${restPct}%`, bgcolor: RESTAURANT, transition: 'width .4s' }} />
          <Box sx={{ width: `${platPct}%`, bgcolor: PLATFORM, transition: 'width .4s' }} />
        </Box>
      </Box>

      {fellBack && (
        <Box sx={{ mb: 2, px: 1.75, py: 1.25, borderRadius: 2, bgcolor: '#fdebec', border: '1px solid #f5c6c9' }}>
          <Typography variant="caption" sx={{ color: '#a12226', fontWeight: 600, display: 'block' }}>
            Delivery fallback — sold as restaurant-delivery, but RushmyFood delivered.
            The split below is what was charged; the delivery fee, tip
            {fbDelta > 0.005 ? ` and the commission difference (re-commissioned at ${effRate}%)` : ''} are
            recorded as a debt of {usd(t.fallbackDebt)}, recovered from their future split payouts.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* What the customer paid */}
        <Box sx={{ ...panel, bgcolor: '#fafbfc' }}>
          <Typography variant="overline" sx={{ color: MUTED, letterSpacing: 1 }}>Customer paid</Typography>
          <MoneyRow label="Items subtotal" value={t.subtotal} />
          <MoneyRow label="Delivery fee" value={t.deliveryFee} />
          <MoneyRow label="Tip" value={t.deliveryTip} />
          <MoneyRow label="Tax" value={t.tax} />
          <MoneyRow label="Service fee" value={t.flatFee} />
          <Box sx={{ borderTop: `1px solid ${LINE}`, mt: 0.75, pt: 0.75 }}>
            <MoneyRow label="Order total" value={t.grandTotal} strong />
          </Box>
        </Box>

        {/* Restaurant's share */}
        <Box sx={{ ...panel, borderTop: `3px solid ${RESTAURANT}` }}>
          <Typography variant="overline" sx={{ color: RESTAURANT, letterSpacing: 1, fontWeight: 700 }}>
            Restaurant receives
          </Typography>
          <MoneyRow
            label="Items subtotal"
            hint={effRate != null ? <>after {rateHint('commission')}</> : (t.commissionRate != null ? `(after ${Number(t.commissionRate)}% commission)` : null)}
            value={rb.subtotalShare}
          />
          <MoneyRow label="Delivery fee" hint={fellBack ? '(owed back — we delivered)' : '(their driver)'} value={rb.deliveryFee} />
          <MoneyRow label="Tip" hint={fellBack ? '(owed back — our rider)' : '(their driver)'} value={rb.deliveryTip} />
          <MoneyRow label="Tax" hint="(they remit)" value={rb.tax} />
          <MoneyRow label="Less store credit spent" hint="(their refund, their cost)" value={Math.abs(Number(rb.storeCredit)) || null} color="#a12226" />
          {Number(t.debtRecovered) > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.4 }}>
              <Typography variant="body2" sx={{ color: '#a12226', minWidth: 0 }}>
                Debt recovery
                <Typography component="span" variant="caption" sx={{ color: MUTED, ml: 0.6 }}>(owed to RushmyFood)</Typography>
              </Typography>
              <Box sx={{ flex: 1, minWidth: 12, borderBottom: `1px dotted ${LINE}`, transform: 'translateY(-4px)' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#a12226', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                −{usd(t.debtRecovered)}
              </Typography>
            </Box>
          )}
          <Box sx={{ borderTop: `1px solid ${LINE}`, mt: 0.75, pt: 0.75 }}>
            <MoneyRow label="Total to restaurant"
              value={Number(t.debtRecovered) > 0 ? Number(t.restaurantCut) - Number(t.debtRecovered) : t.restaurantCut}
              strong color={RESTAURANT} />
          </Box>
        </Box>

        {/* Platform's share */}
        <Box sx={{ ...panel, borderTop: `3px solid ${PLATFORM}` }}>
          <Typography variant="overline" sx={{ color: PLATFORM, letterSpacing: 1, fontWeight: 700 }}>
            RushmyFood receives
          </Typography>
          <MoneyRow
            label="Commission"
            hint={effRate != null ? rateHint('of subtotal') : (t.commissionRate != null ? `(${Number(t.commissionRate)}% of subtotal)` : null)}
            value={pb.commission}
          />
          <MoneyRow label="Service fee" value={pb.flatFee} />
          <MoneyRow label="Delivery fee" hint="(our rider)" value={pb.deliveryFee} />
          <MoneyRow label="Rider tip" hint="(passed to rider)" value={pb.deliveryTip} />
          <MoneyRow label="Tax" hint="(we remit)" value={pb.tax} />
          <MoneyRow label="Debt recovery" hint="(withheld from restaurant)" value={Number(t.debtRecovered) > 0 ? t.debtRecovered : null} />
          <Box sx={{ borderTop: `1px solid ${LINE}`, mt: 0.75, pt: 0.75 }}>
            <MoneyRow label="Total to RushmyFood"
              value={Number(t.debtRecovered) > 0 ? Number(t.platformCut) + Number(t.debtRecovered) : t.platformCut}
              strong color={PLATFORM} />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

// One transaction card: summary row that expands into the full split detail.
function SplitCard({ t }) {
  const [open, setOpen] = useState(false)
  const status = STATUS_META[t.settlementStatus] || { label: t.settlementStatus, dot: MUTED, bg: '#f0f0f3', fg: MUTED }
  const scenario = SCENARIO_META[t.scenario]
  const copy = (e, v) => { e.stopPropagation(); if (v) navigator.clipboard?.writeText(v) }

  return (
    <Box sx={{
      borderRadius: 3, border: `1px solid ${LINE}`, bgcolor: '#fff', mb: 1.5,
      transition: 'box-shadow .2s, border-color .2s',
      boxShadow: open ? '0 8px 28px rgba(28,32,36,.09)' : '0 1px 2px rgba(28,32,36,.04)',
      '&:hover': { borderColor: '#d5d8dc' },
      overflow: 'hidden',
    }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2, px: { xs: 2, md: 3 }, py: 1.75,
          cursor: 'pointer', flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 170 }}>
          <Typography sx={{ fontWeight: 700, color: INK, lineHeight: 1.2 }}>
            {t.orderNumber || `#${t.orderId}`}
          </Typography>
          <Typography variant="caption" sx={{ color: MUTED }}>
            {t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 160, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: INK }}>{t.restaurantName || '—'}</Typography>
          {scenario && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: MUTED }}>
              {Number(t.fallbackDebt) > 0 ? (
                // Delivery fallback: the restaurant was supposed to deliver but we did.
                <>
                  <DeliveryDiningIcon sx={{ fontSize: 14, color: '#a12226' }} />
                  <Typography variant="caption">
                    <s style={{ color: '#9ca3af' }}>{scenario.label}</s>
                    {' '}<span style={{ color: '#a12226', fontWeight: 700 }}>We delivered</span>
                    {t.paymentMethod === 'COD' ? '' : ' · card'}
                  </Typography>
                </>
              ) : (
                <>
                  <scenario.Icon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">{scenario.label}{t.paymentMethod === 'COD' ? '' : ' · card'}</Typography>
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Mini split preview (post-recovery amounts), visible without expanding */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
          {Number(t.fallbackDebt) > 0 && (
            <Tooltip title="Sold as restaurant-delivery but WE delivered — the fee+tip in their share is now a recorded debt">
              <Chip size="small" label={`Owes us ${usd(t.fallbackDebt)}`}
                sx={{ bgcolor: '#fdebec', color: '#a12226', fontWeight: 700, borderRadius: 1.5 }} />
            </Tooltip>
          )}
          {Number(t.debtRecovered) > 0 && (
            <Tooltip title="Part of the restaurant's share was withheld on this charge to repay their debt">
              <Chip size="small" label={`Recovered ${usd(t.debtRecovered)}`}
                sx={{ bgcolor: '#e9f6ee', color: '#166534', fontWeight: 700, borderRadius: 1.5 }} />
            </Tooltip>
          )}
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>Restaurant</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: RESTAURANT, fontVariantNumeric: 'tabular-nums' }}>
              {usd(Number(t.restaurantCut) - (Number(t.debtRecovered) || 0))}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>RushmyFood</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: PLATFORM, fontVariantNumeric: 'tabular-nums' }}>
              {usd(Number(t.platformCut) + (Number(t.debtRecovered) || 0))}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'right', minWidth: 90 }}>
          <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>Total</Typography>
          <Typography sx={{ fontWeight: 800, color: INK, fontVariantNumeric: 'tabular-nums' }}>
            {usd(t.grandTotal)}
          </Typography>
        </Box>

        <Chip
          size="small"
          label={status.label}
          sx={{
            bgcolor: status.bg, color: status.fg, fontWeight: 700, borderRadius: 1.5,
            '& .MuiChip-label': { px: 1.25 },
            '&::before': { content: '""', width: 7, height: 7, borderRadius: '50%', bgcolor: status.dot, ml: 1 },
          }}
        />

        <IconButton size="small" sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
          <KeyboardArrowDownIcon />
        </IconButton>
      </Box>

      <Collapse in={open} timeout={250} unmountOnExit>
        {t.qorTransactionId && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: { xs: 2, md: 3 }, pb: 1 }}>
            <Typography variant="caption" sx={{ color: MUTED }}>Gateway txn</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: INK }}>{t.qorTransactionId}</Typography>
            <Tooltip title="Copy">
              <IconButton size="small" onClick={(e) => copy(e, t.qorTransactionId)}>
                <ContentCopyIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <SplitDetail t={t} />
      </Collapse>
    </Box>
  )
}

export default function QorSplits() {
  const [settlementStatus, setSettlementStatus] = useState('')
  const [scenario, setScenario] = useState('')
  const [search, setSearch] = useState('')
  const [limit, setLimit] = useState(50)

  const { data, loading } = useQuery(GET_TXNS, {
    variables: { settlementStatus: settlementStatus || undefined, includeRecurring: false, limit, offset: 0 },
    fetchPolicy: 'network-only',
  })

  const all = data?.qorTransactions?.rows ?? []
  const count = data?.qorTransactions?.count ?? 0

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return all.filter((t) => {
      if (scenario && t.scenario !== scenario) return false
      if (!q) return true
      return (
        (t.orderNumber || '').toLowerCase().includes(q) ||
        (t.restaurantName || '').toLowerCase().includes(q) ||
        (t.qorTransactionId || '').toLowerCase().includes(q)
      )
    })
  }, [all, scenario, search])

  // Roll-up of what's currently on screen (post-recovery) — honest about scope.
  const totals = useMemo(() => rows.reduce((a, t) => ({
    volume: a.volume + (Number(t.grandTotal) || 0),
    platform: a.platform + (Number(t.platformCut) || 0) + (Number(t.debtRecovered) || 0),
    restaurant: a.restaurant + (Number(t.restaurantCut) || 0) - (Number(t.debtRecovered) || 0),
  }), { volume: 0, platform: 0, restaurant: 0 }), [rows])

  const kpi = (label, value, color) => (
    <Box sx={{ flex: 1, minWidth: 150, borderRadius: 3, p: 2, bgcolor: '#fff', border: `1px solid ${LINE}` }}>
      <Typography variant="caption" sx={{ color: MUTED }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 800, color: color || INK, fontVariantNumeric: 'tabular-nums' }}>
        {usd(value)}
      </Typography>
    </Box>
  )

  return (
    <Box sx={{ bgcolor: '#f7f8fa', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2.5, display: 'grid', placeItems: 'center',
            background: `linear-gradient(135deg, ${RESTAURANT}, ${PLATFORM})`, color: '#fff',
          }}>
            <CallSplitIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: INK, lineHeight: 1.1 }}>Transaction Splits</Typography>
            <Typography variant="body2" sx={{ color: MUTED }}>
              Every order&apos;s money, itemized — what the customer paid, what the restaurant gets, what RushmyFood keeps.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, my: 2.5, flexWrap: 'wrap' }}>
          {kpi('Volume (shown)', totals.volume)}
          {kpi('To restaurants', totals.restaurant, RESTAURANT)}
          {kpi('To RushmyFood', totals.platform, PLATFORM)}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small" placeholder="Order #, restaurant, txn id…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 260, '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment>,
            }}
          />
          {/* displayEmpty + shrink so the "All …" choice is always visible —
              without them an empty-string value renders as a blank white box. */}
          <TextField
            select size="small" label="Status" value={settlementStatus}
            onChange={(e) => setSettlementStatus(e.target.value)}
            sx={{ width: 220, '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {Object.entries(STATUS_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Order type" value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            sx={{ width: 200, '& .MuiOutlinedInput-root': { bgcolor: '#fff' } }}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">All types</MenuItem>
            {Object.entries(SCENARIO_META).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          <Typography variant="caption" sx={{ color: MUTED, ml: 'auto' }}>
            {rows.length} of {count} transactions
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, borderRadius: 3, border: `1px dashed ${LINE}`, bgcolor: '#fff' }}>
            <Typography sx={{ fontWeight: 700, color: INK }}>No transactions yet</Typography>
            <Typography variant="body2" sx={{ color: MUTED }}>
              Card and cash orders appear here with their full split breakdown once placed.
            </Typography>
          </Box>
        ) : (
          <>
            {rows.map((t) => <SplitCard key={t._id} t={t} />)}
            {all.length < count && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button variant="outlined" onClick={() => setLimit(limit + 50)}>Load more</Button>
              </Box>
            )}
          </>
        )}

        <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 3 }}>
          Failed / declined charge attempts never become orders, so they are not listed here —
          see QorCommerce Responses for raw gateway declines. Void, refund and settlement
          actions live on the QorCommerce Transactions page.
        </Typography>
      </Container>
    </Box>
  )
}